import {
  BankApplicationStatus,
  GoToError,
  MechanismType,
  Query,
  SupplementalDocumentRequest,
  WhereToGoDestination,
  WhereToGoDestinationTypenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import {
  SourceName,
  useApplicationTrackErrorEvents,
} from 'features/application/hooks/useApplicationTrackErrorEvents';
import { useStatusChangedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import Socure from 'features/signup/ApplicationStatus/Review/Socure/Socure';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import GetReactivationDocumentRequests from 'services/graphql/queries/GetReactivationDocumentRequests';
import WhereToGo from 'services/graphql/queries/WhereToGo';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { useQuery } from 'urql';

interface ReviewProps {
  bankApplicationStatus: string;
  onSocureFlowCompleted: () => void;
  isReactivation: boolean;
  isReapplication?: boolean;
  bankApplicationId?: string;
}

const ApplicationReview = ({
  bankApplicationStatus,
  onSocureFlowCompleted,
  isReactivation,
  isReapplication = false,
  bankApplicationId,
}: ReviewProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'review',
  });
  const { logErrorEvent } = useApplicationTrackErrorEvents();

  const [socureDocRequest, setSocureDocRequest] =
    useState<SupplementalDocumentRequest | null>(null);

  const [{ data, fetching }, refetchWhereToGo] = useQuery<Query>({
    query: WhereToGo, //for signup flow
    requestPolicy: 'network-only',
    pause: isReactivation,
  });

  const [
    {
      data: documentRequestsData,
      error: documentRequestsError,
      fetching: documentRequestsFetching,
    },
    refetchDocumentRequests,
  ] = useQuery<Query>({
    query: GetReactivationDocumentRequests, // for reactivation flow
    variables: { externalId: bankApplicationId },
    requestPolicy: 'network-only',
    pause: !isReactivation,
  });

  const whereToGo = data?.whereToGo as WhereToGoDestination;
  const whereToGoError = data?.whereToGo as GoToError;

  const reactivationDocumentRequestsData =
    documentRequestsData?.reactivationDocumentRequests as {
      requests?: Array<SupplementalDocumentRequest | null>;
    };

  const { handleLogout } = useContext<AuthContextProps>(AuthContext);

  useViewDidFocusScreenAnalyticsEvent({
    key: 'applicationInReview',
    customAttributes: {
      ...(isReactivation && {
        reactivation_flow: 'true',
      }),
      ...(isReapplication && {
        reapplication_flow: 'true',
      }),
    },
  });

  useStatusChangedFocusAnalytics({
    analyticsId: 'statusChanged',
    customAttributes: {
      status: 'in_review',
      ...(isReactivation && {
        reactivation_flow: 'true',
      }),
      ...(isReapplication && {
        reapplication_flow: 'true',
      }),
    },
  });

  const handleLogoutAnalytics = usePressableAnalytics({
    analyticsId: 'logoutClicked',
    customAttributes: {
      ...(isReactivation && {
        reactivation_flow: 'true',
      }),
      ...(isReapplication && {
        reapplication_flow: 'true',
      }),
    },
  });

  const onLogoutPress = () => {
    handleLogoutAnalytics();
    void handleLogout();
  };

  useEffect(() => {
    if (whereToGoError?.message || documentRequestsError?.message) {
      logErrorEvent(
        isReactivation
          ? documentRequestsError?.message || ''
          : whereToGoError?.message || '',
        isReactivation
          ? SourceName.REACTIVATION
          : SourceName.APPLICATION_DECISION,
      );
    }
  }, [whereToGoError, documentRequestsError, logErrorEvent, isReactivation]);

  useEffect(() => {
    if (isReactivation) {
      reactivationDocumentRequestsData?.requests?.forEach(
        (docRequest?: SupplementalDocumentRequest | null) => {
          if (docRequest) {
            const { mechanism, url } = docRequest;
            if (mechanism === MechanismType.Socure && url) {
              setSocureDocRequest(docRequest);
            }
          }
        },
      );
    } else if (
      whereToGo?.__typename === WhereToGoDestinationTypenames.GoToIdvRequest
    ) {
      whereToGo?.requests?.forEach(
        (docRequest: SupplementalDocumentRequest) => {
          const { mechanism, url } = docRequest;
          if (mechanism === MechanismType.Socure && url) {
            setSocureDocRequest(docRequest);
          }
        },
      );
    }
  }, [whereToGo, isReactivation, reactivationDocumentRequestsData]);

  const handleBackButtonAction = () => {
    if (isReactivation) {
      router.replace('/reactivation');
    } else {
      router.replace('/login');
    }
  };

  useBackHandler(handleBackButtonAction);

  const showSocureReviewScreen =
    (bankApplicationStatus.toLowerCase() ===
      BankApplicationStatus.InReview.toLowerCase() ||
      bankApplicationStatus.toLowerCase() ===
        BankApplicationStatus.Pending.toLowerCase()) &&
    (!isReactivation
      ? whereToGo?.__typename === WhereToGoDestinationTypenames.GoToOnboarding
      : !reactivationDocumentRequestsData?.requests?.length);

  if (fetching || documentRequestsFetching) {
    return null;
  }

  return !showSocureReviewScreen ? (
    <Socure
      socureDocRequest={socureDocRequest as SupplementalDocumentRequest}
      onSocureFlowCompleted={onSocureFlowCompleted}
      isReactivation={isReactivation}
      onClose={() => {
        // refetch the document to get the latest url avoiding session timeout
        if (isReactivation) {
          refetchDocumentRequests();
        } else {
          refetchWhereToGo();
        }
      }}
    />
  ) : (
    <SuccessScreen
      primaryButton={{
        label: translatedText('logOut'),
        onPress: onLogoutPress,
      }}
      visual="CHECK_A"
      titleText={translatedText('title')}
      content={
        <BodyBaseText numberOfLines={0} style={styles.description}>
          {translatedText('description')}
        </BodyBaseText>
      }
      screenAnalyticsEventKey="/signup/socure/application-in-review"
    />
  );
};

const styles = StyleSheet.create({
  description: {
    textAlign: 'center',
  },
});

export default ApplicationReview;
